import './styles.scss';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

type DropdownProps = {
  children: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  classNames?: string;
  onClose?: () => void;
  anchorEl?: HTMLElement | null;
  portalId?: string;
  isOpenOther?: boolean | false;
};

const DEFAULT_MARGIN_RIGHT = 16;

const DropdownPortal = forwardRef<HTMLDivElement, DropdownProps>(
  ({ children, isOpen, setIsOpen, classNames, onClose, anchorEl, portalId, isOpenOther }, ref) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const dropdownRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;

    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    const uniqueId = portalId || `dropdown-portal-${Math.random().toString(36).substr(2, 9)}`;

    useEffect(() => {
      if (anchorEl && isOpen && dropdownRef.current) {
        const calculatePosition = () => {
          const rect = anchorEl.getBoundingClientRect();
          const dropdownEl = dropdownRef.current;

          if (!dropdownEl) return;

          const width = dropdownEl.offsetWidth;

          let left = rect.left;

          const isOverflowingRight = left + width + DEFAULT_MARGIN_RIGHT > window.innerWidth;

          if (isOverflowingRight) {
            left = window.innerWidth - width - DEFAULT_MARGIN_RIGHT;
          }

          setPosition({ top: rect.bottom + window.scrollY, left });
        };

        calculatePosition();
      }
    }, [
      anchorEl,
      isOpen,
      dropdownRef.current?.offsetWidth,
      dropdownRef.current?.offsetHeight,
      isOpenOther,
    ]);

    const handleOnClose = useCallback(() => {
      if (isOpen && onClose) {
        onClose();
      }
    }, [isOpen, onClose]);

    useEffect(() => {
      if (!isOpen) {
        setPosition(null);
      }
    }, [isOpen]);

    useEffect(() => {
      const handleClickOutside = (event: any) => {
        const isSelectClick =
          event.target.closest('.dropdown-portal-ignore') ||
          event.target?.closest('.menu-select-container');
        const dropdownEl = document.getElementById(uniqueId);
        if (
          dropdownEl &&
          !dropdownEl.contains(event.target) &&
          !isSelectClick &&
          !anchorEl?.contains(event.target)
        ) {
          setIsOpen(false);
          handleOnClose();
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [handleOnClose, uniqueId, isOpen, anchorEl]);

    if (!isOpen) return null;

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onClose) {
        onClose();
      }
      setIsOpen(false);
    };

    const DropdownIgnore = () => {
      return ReactDOM.createPortal(
        <div className="dropdown-portal-ignore" onClick={handleClose}>
          <span>Click to close</span>
        </div>,
        document.body,
      );
    };

    const RenderDropdown = () => {
      return ReactDOM.createPortal(
        <div
          id={uniqueId}
          className={`dropdown-portal ${classNames || ''}`}
          style={position ? { position: 'absolute', top: position.top, left: position.left } : {}}
          onClick={(e) => e.stopPropagation()}
          ref={dropdownRef}
        >
          <div className="dropdown-content">{children}</div>
        </div>,
        document.body,
      );
    };

    return (
      <>
        <DropdownIgnore />
        <RenderDropdown />
      </>
    );
  },
);

DropdownPortal.displayName = 'DropdownPortal';

export { DropdownPortal };
